import { useNavigate } from 'react-router-dom';
import { Users, Plus, X, IndianRupee, User, Calendar, Coins, ChevronRight } from 'lucide-react';
import SummaryApi from '../../common';
import { useState, useEffect } from 'react';
import fetchWithAuth from '../../common/fetchWithAuth';
import { toast } from 'react-toastify';

export default function BhishiManagement() {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        ownerName: "",
        membersCount: "",
        startDate: "",
        bharniDate: "",
        bharnaAmount: "",
        bhishiAmount: ""
    });

    // Reset form
    const resetForm = () => {
        setFormData({
            ownerName: "",
            membersCount: "",
            startDate: "",
            bharniDate: "",
            bharnaAmount: "",
            bhishiAmount: ""
        });
    };

    // Fetch Groups
    const fetchGroups = async () => {
        try {
            const response = await fetchWithAuth(SummaryApi.getAllBhishiGroups.url, {
                method: SummaryApi.getAllBhishiGroups.method
            });
            const data = await response.json();
            if (Array.isArray(data)) {
                setGroups(data);
            }
        } catch (error) {
            console.error("Error fetching groups:", error);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await fetchWithAuth(SummaryApi.createBhishiGroup.url, {
                method: SummaryApi.createBhishiGroup.method,
                body: JSON.stringify(formData)
            });
            const data = await response.json();

            if (data.success) {
                fetchGroups();
                setShowModal(false);
                resetForm();
                toast.success("Group created successfully!");
            } else {
                toast.error(data.message || "Failed to create group");
            }
        } catch (error) {
            console.error("Error creating group:", error);
            toast.error("Error creating group");
        } finally {
            setLoading(false);
        }
    };

    // Calculate totals for stats
    const totalCollection = groups.reduce((acc, curr) => acc + (Number(curr.bhishiAmount) || 0), 0);
    const totalBharnaAmount = groups.reduce((acc, curr) => acc + (Number(curr.bharnaAmount) || 0), 0);

    return (
        <div className="w-full pb-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                        <span className="p-2 bg-blue-100 rounded-xl text-blue-600">
                            <Users size={32} />
                        </span>
                        Bhishi Management
                    </h1>
                    <p className="text-gray-500 text-sm mt-2 ml-1">
                        Manage your chit fund groups and financial details.
                    </p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-medium transition-all shadow-lg shadow-blue-200"
                >
                    <Plus size={20} />
                    Add Owner / Group
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                            <Users size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-medium">Active Groups</p>
                            <h3 className="text-2xl font-bold text-gray-800">{groups.length}</h3>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                            <IndianRupee size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-medium">Total Bhishi Amount</p>
                            <h3 className="text-2xl font-bold text-gray-800">
                                {totalCollection.toLocaleString('en-IN')}
                            </h3>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                            <IndianRupee size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-medium">Total Bharni Amount</p>
                            <h3 className="text-2xl font-bold text-gray-800">
                                {totalBharnaAmount.toLocaleString('en-IN')}
                            </h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Groups Grid */}
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Users size={20} className="text-gray-400" />
                Groups List
            </h2>

            {groups.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-100 border-dashed">
                    <p className="text-gray-500">No groups found. Add a new owner to get started.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {groups.map((group) => (
                        <div
                            key={group._id}
                            onClick={() => navigate(`/dashboard/bhishi/${group._id}`)}
                            className="bg-white rounded-2xl border border-gray-100 hover:border-blue-200 shadow-sm hover:shadow-md transition-all p-6 group cursor-pointer relative overflow-hidden"
                        >
                            <div className="flex justify-between items-start mb-4 border-b border-gray-50 pb-4">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Owner Name</p>
                                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                        <User size={18} className="text-blue-500" />
                                        {group.ownerName}
                                    </h3>
                                </div>
                                <div className="text-right flex flex-col items-end gap-1">
                                    <button className="p-1.5 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors">
                                        <ChevronRight size={18} />
                                    </button>
                                    <span className="px-3 py-1 bg-gray-50 text-gray-600 rounded-lg text-xs font-bold mt-1">
                                        {group.membersCount} Members
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-gray-50 p-3 rounded-xl">
                                        <p className="text-xs text-gray-500 mb-1">Start Date</p>
                                        <div className="font-medium text-gray-700 text-sm flex items-center gap-1.5">
                                            <Calendar size={14} className="text-gray-400" />
                                            {new Date(group.startDate).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-xl">
                                        <p className="text-xs text-gray-500 mb-1">Bharni Date</p>
                                        <div className="font-medium text-gray-700 text-sm flex items-center gap-1.5">
                                            <Calendar size={14} className="text-gray-400" />
                                            {new Date(group.bharniDate).toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Bharna Amt</p>
                                        <p className="text-lg font-bold text-emerald-600 flex items-center">
                                            <IndianRupee size={14} />
                                            {Number(group.bharnaAmount).toLocaleString()}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs text-gray-500 mb-1">Total Bhishi</p>
                                        <p className="text-lg font-bold text-indigo-600 flex items-center justify-end">
                                            <IndianRupee size={14} />
                                            {Number(group.bhishiAmount).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Group Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm">
                    <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl animate-in fade-in zoom-in-95 flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                            <h3 className="text-lg font-bold text-gray-800">Add New Owner Details</h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name</label>
                                    <input
                                        required
                                        name="ownerName"
                                        value={formData.ownerName}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                                        placeholder="Enter owner name"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Members Count</label>
                                        <input
                                            required
                                            type="number"
                                            name="membersCount"
                                            value={formData.membersCount}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                                            placeholder="e.g. 20"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                        <input
                                            required
                                            type="date"
                                            name="startDate"
                                            value={formData.startDate}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Bharni Date</label>
                                    <input
                                        required
                                        type="date"
                                        name="bharniDate"
                                        value={formData.bharniDate}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Date for collection.</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Bharna Amount</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                                            <input
                                                required
                                                type="number"
                                                name="bharnaAmount"
                                                value={formData.bharnaAmount}
                                                onChange={handleInputChange}
                                                className="w-full pl-8 pr-4 py-2 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Bhishi Amount</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                                            <input
                                                required
                                                type="number"
                                                name="bhishiAmount"
                                                value={formData.bhishiAmount}
                                                onChange={handleInputChange}
                                                className="w-full pl-8 pr-4 py-2 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                                                placeholder="Total"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-6 py-2 border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg shadow-blue-200 transition-colors font-medium disabled:opacity-70"
                                >
                                    {loading ? "Saving..." : "Save Details"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
